import { XCircleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useGetTokenUsage } from '@/entities/ailogs/api/generated/api';
import { useIsAdmin } from '@/entities/session/model/useIsAdmin';
import { resolveBillingPeriodType } from '@/shared/lib/billing-period-type/resolveBillingPeriodType';
import { StatusCard } from '@/shared/ui';

/**
 * トークン制限超過ビュー
 *
 * AI機能の実行時にトークン制限超過（402）が発生した場合に表示する。
 * 管理者の場合のみ「プランと使用量を確認」ボタンを表示する。
 * 契約サイクル（monthly / yearly）に応じて description を切り替える。
 *
 * `/ai-logs/token-usage` は管理者専用 API（@require_tenant_role ADMIN）のため、
 * `useGetTokenUsage` は admin 確定時のみ enabled にする。
 * editor では billing_period_type が undefined となり monthly フォールバックされる
 * （editor で yearly テナントの正確な文言は出ないが、機能影響はない）。
 */
export const TokenLimitExceededView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const { data: tokenUsage } = useGetTokenUsage({
    query: { enabled: isAdmin === true },
  });

  // API レスポンス + デバッグ環境の override + 未取得フォールバックを resolveBillingPeriodType で集約
  const billingPeriodType = resolveBillingPeriodType(tokenUsage?.plan.billing_period_type);
  const descriptionMap = {
    monthly: t('ai.errors.token-limit-exceeded-description-monthly'),
    yearly: t('ai.errors.token-limit-exceeded-description-yearly'),
  };

  return (
    <StatusCard
      title={t('plan-usage.alert.limit-reached-title')}
      description={descriptionMap[billingPeriodType]}
      icon={<XCircleIcon className="size-8 text-text-error" />}
      buttonText={isAdmin === true ? t('ai.errors.token-limit-exceeded-button') : undefined}
      onButtonClick={isAdmin === true ? () => navigate({ to: '/plan-usage' }) : undefined}
    />
  );
};
